// Utility functions for handling multiple medical readings across visits

/**
 * Calculate average values from visit-based medical readings
 * @param {Array} visitReadings - Array of visit objects with readings
 * @param {string} visitId - Optional: specific visit ID to analyze
 * @returns {Object} - Average values and reading count
 */
export const calculateAverageReadings = (visitReadings = [], visitId = null) => {
  if (!visitReadings || visitReadings.length === 0) {
    return {
      avgSystolicBP: null,
      avgDiastolicBP: null,
      avgHeartRate: null,
      readingCount: 0,
      visitCount: 0,
      latestReading: null,
      latestVisit: null
    };
  }

  // Filter to specific visit if requested
  const visitsToAnalyze = visitId 
    ? visitReadings.filter(visit => visit.visitId === visitId)
    : visitReadings;

  // Flatten all readings from all visits
  const allReadings = visitsToAnalyze.reduce((acc, visit) => {
    const source = Array.isArray(visit.readings) && visit.readings.length ? visit.readings : (visit.vitalSigns || []);
    if (source && source.length > 0) {
      acc.push(...source.map(reading => ({
        ...reading,
        visitId: visit.visitId,
        visitDate: visit.visitDate,
        eventName: visit.eventName
      })));
    }
    return acc;
  }, []);

  if (allReadings.length === 0) {
    return {
      avgSystolicBP: null,
      avgDiastolicBP: null,
      avgHeartRate: null,
      readingCount: 0,
      visitCount: visitsToAnalyze.length,
      latestReading: null,
      latestVisit: null
    };
  }

  // Sort by timestamp to get latest
  const sortedReadings = allReadings.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  const latestReading = sortedReadings[0];
  
  // Find latest visit
  const sortedVisits = visitsToAnalyze.sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));
  const latestVisit = sortedVisits[0];

  const totals = allReadings.reduce((acc, reading) => {
    acc.systolic += reading.systolicBP || 0;
    acc.diastolic += reading.diastolicBP || 0;
    acc.heartRate += reading.heartRate || 0;
    return acc;
  }, { systolic: 0, diastolic: 0, heartRate: 0 });

  const count = allReadings.length;

  return {
    avgSystolicBP: Math.round(totals.systolic / count),
    avgDiastolicBP: Math.round(totals.diastolic / count),
    avgHeartRate: Math.round(totals.heartRate / count),
    readingCount: count,
    visitCount: visitsToAnalyze.length,
    latestReading,
    latestVisit,
    firstReading: sortedReadings[sortedReadings.length - 1]
  };
};

/**
 * Get readings for a specific visit
 * @param {Array} visitReadings - Array of visit objects
 * @param {string} visitId - Visit ID to get readings for
 * @returns {Object} - Visit data with readings
 */
export const getVisitReadings = (visitReadings = [], visitId) => {
  return visitReadings.find(visit => visit.visitId === visitId) || null;
};

/**
 * Get all visits sorted by date (newest first)
 * @param {Array} visitReadings - Array of visit objects
 * @returns {Array} - Sorted visits
 */
export const getSortedVisits = (visitReadings = []) => {
  return [...visitReadings].sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate));
};

/**
 * Analyze trends across visits
 * @param {Array} visitReadings - Array of visit objects
 * @returns {Object} - Trend analysis across visits
 */
export const analyzeVisitTrends = (visitReadings = []) => {
  if (visitReadings.length < 2) {
    return { 
      hasTrend: false, 
      message: 'Need at least 2 visits for trend analysis',
      visitCount: visitReadings.length 
    };
  }

  const sortedVisits = getSortedVisits(visitReadings);
  const latest = sortedVisits[0];
  const oldest = sortedVisits[sortedVisits.length - 1];

  // Compare average readings between first and last visit
  const latestAvg = {
    systolic: latest.averageSystolic || calculateAverageFromReadings((latest.readings && latest.readings.length ? latest.readings : latest.vitalSigns), 'systolicBP'),
    diastolic: latest.averageDiastolic || calculateAverageFromReadings((latest.readings && latest.readings.length ? latest.readings : latest.vitalSigns), 'diastolicBP'),
    heartRate: latest.averageHeartRate || calculateAverageFromReadings((latest.readings && latest.readings.length ? latest.readings : latest.vitalSigns), 'heartRate')
  };

  const oldestAvg = {
    systolic: oldest.averageSystolic || calculateAverageFromReadings((oldest.readings && oldest.readings.length ? oldest.readings : oldest.vitalSigns), 'systolicBP'),
    diastolic: oldest.averageDiastolic || calculateAverageFromReadings((oldest.readings && oldest.readings.length ? oldest.readings : oldest.vitalSigns), 'diastolicBP'),
    heartRate: oldest.averageHeartRate || calculateAverageFromReadings((oldest.readings && oldest.readings.length ? oldest.readings : oldest.vitalSigns), 'heartRate')
  };

  const trends = [];
  const systolicChange = latestAvg.systolic - oldestAvg.systolic;
  const diastolicChange = latestAvg.diastolic - oldestAvg.diastolic;
  const heartRateChange = latestAvg.heartRate - oldestAvg.heartRate;

  if (Math.abs(systolicChange) >= 10) {
    trends.push({
      metric: 'Systolic BP',
      change: systolicChange,
      direction: systolicChange > 0 ? 'increasing' : 'decreasing',
      significance: Math.abs(systolicChange) >= 20 ? 'significant' : 'moderate'
    });
  }

  if (Math.abs(diastolicChange) >= 5) {
    trends.push({
      metric: 'Diastolic BP',
      change: diastolicChange,
      direction: diastolicChange > 0 ? 'increasing' : 'decreasing',
      significance: Math.abs(diastolicChange) >= 10 ? 'significant' : 'moderate'
    });
  }

  if (Math.abs(heartRateChange) >= 10) {
    trends.push({
      metric: 'Heart Rate',
      change: heartRateChange,
      direction: heartRateChange > 0 ? 'increasing' : 'decreasing',
      significance: Math.abs(heartRateChange) >= 20 ? 'significant' : 'moderate'
    });
  }

  const timeSpan = new Date(latest.visitDate) - new Date(oldest.visitDate);
  const daysBetween = Math.floor(timeSpan / (1000 * 60 * 60 * 24));

  return {
    hasTrend: trends.length > 0,
    trends,
    visitCount: visitReadings.length,
    timeSpan: daysBetween,
    latestVisit: latest.visitDate,
    oldestVisit: oldest.visitDate,
    overallAssessment: getOverallTrendAssessment(trends)
  };
};

/**
 * Helper function to calculate average from readings array
 */
const calculateAverageFromReadings = (readings = [], field) => {
  if (!readings || readings.length === 0) return null;
  const sum = readings.reduce((acc, reading) => acc + (reading[field] || 0), 0);
  return Math.round(sum / readings.length);
};

/**
 * Get overall trend assessment
 */
const getOverallTrendAssessment = (trends) => {
  if (trends.length === 0) return 'stable';
  
  const significantTrends = trends.filter(t => t.significance === 'significant');
  const increasingTrends = trends.filter(t => t.direction === 'increasing');
  const decreasingTrends = trends.filter(t => t.direction === 'decreasing');

  if (significantTrends.length > 0) {
    if (increasingTrends.length > decreasingTrends.length) {
      return 'deteriorating';
    } else if (decreasingTrends.length > increasingTrends.length) {
      return 'improving';
    }
  }
  
  return 'changing';
};

/**
 * Get blood pressure category based on AHA guidelines
 * @param {number} systolic - Systolic BP
 * @param {number} diastolic - Diastolic BP
 * @returns {Object} - Category info with text, color, and severity
 */
export const getBPCategory = (systolic, diastolic) => {
  if (systolic >= 180 || diastolic >= 110) {
    return { 
      text: 'Hypertensive Crisis', 
      color: '#f5222d', 
      severity: 'critical',
      points: 30 
    };
  } else if (systolic >= 140 || diastolic >= 90) {
    return { 
      text: 'Stage 2 Hypertension', 
      color: '#fa541c', 
      severity: 'high',
      points: 25 
    };
  } else if (systolic >= 130 || diastolic >= 80) {
    return { 
      text: 'Stage 1 Hypertension', 
      color: '#fa8c16', 
      severity: 'moderate',
      points: 15 
    };
  } else if (systolic >= 120) {
    return { 
      text: 'Elevated', 
      color: '#faad14', 
      severity: 'low',
      points: 5 
    };
  } else {
    return { 
      text: 'Normal', 
      color: '#52c41a', 
      severity: 'normal',
      points: 0 
    };
  }
};

/**
 * Get heart rate category
 * @param {number} heartRate - Heart rate in BPM
 * @returns {Object} - Category info
 */
export const getHRCategory = (heartRate) => {
  if (heartRate < 60) {
    return { text: 'Bradycardia', color: '#1890ff', severity: 'low' };
  } else if (heartRate > 100) {
    return { text: 'Tachycardia', color: '#fa541c', severity: 'moderate' };
  } else {
    return { text: 'Normal', color: '#52c41a', severity: 'normal' };
  }
};

/**
 * Check if readings within a visit show concerning trends
 * @param {Array} readings - Array of readings within a single visit
 * @returns {Object} - Trend analysis within visit
 */
export const analyzeReadingTrends = (readings = []) => {
  if (readings.length < 2) {
    return { hasTrend: false, message: 'Need at least 2 readings for trend analysis' };
  }

  const sorted = [...readings].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const systolicChange = last.systolicBP - first.systolicBP;
  const diastolicChange = last.diastolicBP - first.diastolicBP;
  const hrChange = last.heartRate - first.heartRate;

  const trends = [];

  if (systolicChange >= 20) trends.push('Systolic BP trending upward during visit');
  if (systolicChange <= -20) trends.push('Systolic BP trending downward during visit');
  if (diastolicChange >= 15) trends.push('Diastolic BP trending upward during visit');
  if (diastolicChange <= -15) trends.push('Diastolic BP trending downward during visit');
  if (hrChange >= 20) trends.push('Heart rate trending upward during visit');
  if (hrChange <= -20) trends.push('Heart rate trending downward during visit');

  return {
    hasTrend: trends.length > 0,
    trends,
    systolicChange,
    diastolicChange,
    hrChange,
    readingSpan: sorted.length,
    timeSpan: new Date(last.timestamp) - new Date(first.timestamp)
  };
};

/**
 * Format reading for display
 * @param {Object} reading - Single reading object
 * @returns {Object} - Formatted reading
 */
export const formatReading = (reading) => {
  if (!reading) return null;

  return {
    ...reading,
    bloodPressure: `${reading.systolicBP}/${reading.diastolicBP}`,
    bpCategory: getBPCategory(reading.systolicBP, reading.diastolicBP),
    hrCategory: getHRCategory(reading.heartRate),
    formattedTime: new Date(reading.timestamp).toLocaleString(),
    relativeTime: getRelativeTime(reading.timestamp)
  };
};

/**
 * Format visit data for display
 * @param {Object} visit - Visit object with readings
 * @returns {Object} - Formatted visit data
 */
export const formatVisit = (visit) => {
  if (!visit) return null;

  const avgReadings = {
    systolic: visit.averageSystolic || calculateAverageFromReadings((visit.readings && visit.readings.length ? visit.readings : visit.vitalSigns), 'systolicBP'),
    diastolic: visit.averageDiastolic || calculateAverageFromReadings((visit.readings && visit.readings.length ? visit.readings : visit.vitalSigns), 'diastolicBP'),
    heartRate: visit.averageHeartRate || calculateAverageFromReadings((visit.readings && visit.readings.length ? visit.readings : visit.vitalSigns), 'heartRate')
  };

  return {
    ...visit,
    formattedDate: new Date(visit.visitDate).toLocaleDateString(),
    formattedTime: new Date(visit.visitDate).toLocaleTimeString(),
    relativeDate: getRelativeTime(visit.visitDate),
    readingCount: visit.readings?.length || 0,
    avgBP: avgReadings.systolic && avgReadings.diastolic 
      ? `${avgReadings.systolic}/${avgReadings.diastolic}` 
      : null,
    avgBPCategory: avgReadings.systolic && avgReadings.diastolic
      ? getBPCategory(avgReadings.systolic, avgReadings.diastolic)
      : null,
    avgHRCategory: avgReadings.heartRate 
      ? getHRCategory(avgReadings.heartRate)
      : null,
    ...avgReadings
  };
};

/**
 * Get relative time string (e.g., "5 minutes ago")
 * @param {string|Date} timestamp - Timestamp
 * @returns {string} - Relative time string
 */
const getRelativeTime = (timestamp) => {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hours ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 30) return `${diffDays} days ago`;
  
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths} months ago`;
  
  const diffYears = Math.floor(diffMonths / 12);
  return `${diffYears} years ago`;
};
